import classNames from "classnames";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type HTMLAttributes
} from "react";
import { SelectionItem } from "../../../../Blocks/ProvetOnlineBooking/components/shared/selectionItem";
import { FormSelectItem } from "../../../../Blocks/ProvetOnlineBooking/types";
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonType,
  ButtonVariant
} from "../../../../Shared/Buttons/React";
import { SearchInput } from "../../SearchInput/React";
import { SelectInputTheme } from "./selectInputTheme";

interface SelectInputProps {
  items?: FormSelectItem[];
  id?: string;
  name?: string;
  title?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  value?: string;
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
  itemLimitWarning?: string;
  className?: string;
  containerClassName?: string;
  buttonId?: string;
  onSelectedChanged?: (item: FormSelectItem) => void;
  onBlur?: () => void;
  onSearchCallback?: (searchValue: string) => void;
  additionalAttributes?: HTMLAttributes<HTMLDivElement>;
  theme?: SelectInputTheme;
}

export const SelectInput = ({
  items = [],
  id,
  name,
  title,
  placeholder = "",
  searchPlaceholder = "",
  value,
  disabled = false,
  required = false,
  invalid = false,
  itemLimitWarning,
  className = "",
  containerClassName = "",
  buttonId,
  onSelectedChanged,
  onBlur,
  onSearchCallback,
  additionalAttributes,
  theme = SelectInputTheme.Default
}: SelectInputProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const selectRef = useRef<HTMLDivElement>(null);
  const selectInputRef = useRef<HTMLSelectElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectHeadRef = useRef<HTMLDivElement>(null); // Add ref for the focusable div

  const defaultOption: FormSelectItem = useMemo(
    () => ({
      value: "",
      caption: placeholder,
      description: ""
    }),
    [placeholder]
  );

  const allowSearch = useMemo(
    () => items.length > 1 || searchValue.length > 0,
    [items, searchValue]
  );

  const selected = useMemo(() => {
    if (value && value !== "-1") {
      const item = items.find(item => item.value.toLowerCase() === value.toLowerCase());
      if (item) {
        return item;
      }
    }
    return defaultOption;
  }, [value, items, defaultOption]);

  const hasSelected = useMemo(
    () => selected && selected.value && selected.value !== defaultOption.value,
    [selected, defaultOption]
  );

  const selectItem = useCallback(
    (item: FormSelectItem) => {
      setIsOpen(false);
      resetSearch();

      if (onSelectedChanged) {
        onSelectedChanged(item);
      }
    },
    [onSelectedChanged]
  );

  const resetValue = useCallback(() => {
    if (onSelectedChanged) {
      onSelectedChanged(defaultOption);
    }
  }, [onSelectedChanged, defaultOption]);

  const toggleSelect = useCallback(() => {
    if (disabled) {
      return;
    }

    setIsOpen(prev => {
      if (prev) {
        resetSearch();
      }
      return !prev;
    });
  }, [disabled]);

  const resetSearch = useCallback(() => {
    if (searchValue) {
      setSearchValue("");
      if (onSearchCallback) {
        onSearchCallback("");
      }
    }
  }, [searchValue, onSearchCallback]);

  const handleSearchChange = useCallback(
    (input?: string) => {
      setSearchValue(input || "");
      if (onSearchCallback) {
        onSearchCallback(input || "");
      }
    },
    [onSearchCallback]
  );

  const handleSearchSubmit = useCallback(() => {
    if (onSearchCallback) {
      onSearchCallback(searchValue);
    }
  }, [onSearchCallback, searchValue]);

  // Add animation effect for dropdown
  useEffect(() => {
    if (dropdownRef.current) {
      const dropdown = dropdownRef.current;

      if (isOpen) {
        // Opening animation
        dropdown.style.display = "block";
        dropdown.style.opacity = "0";
        dropdown.style.transform = "translateY(-10px)";

        // Force reflow
        dropdown.offsetHeight;

        dropdown.style.transition = "opacity 200ms ease-out, transform 200ms ease-out";
        dropdown.style.opacity = "1";
        dropdown.style.transform = "translateY(0)";
      } else {
        // Closing animation
        dropdown.style.transition = "opacity 150ms ease-in, transform 150ms ease-in";
        dropdown.style.opacity = "0";
        dropdown.style.transform = "translateY(-10px)";

        setTimeout(() => {
          if (!isOpen) {
            // Check if still closed
            dropdown.style.display = "none";
          }
        }, 150);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        resetSearch();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, resetSearch]);

  return (
    <div
      ref={selectRef}
      title={title}
      className={classNames("relative", containerClassName)}
      {...additionalAttributes}
    >
      <div>
        <div
          ref={selectHeadRef}
          className={classNames(
            "w-full p-xs border rounded-tl-br-sm text-t-sm cursor-pointer",
            "transition-short-in-out hover:border-dark-blue",
            "flex items-center justify-between focus-outline",
            // Add programmatic focus outline using data attribute
            "data-[programmatic-focus=true]:outline-4 data-[programmatic-focus=true]:outline-dark-blue/75",
            {
              "border-neutral-300": !hasSelected,
              "border-turquoise-600": hasSelected && theme === SelectInputTheme.Default,
              "border-green-600": hasSelected && theme === SelectInputTheme.Green,
              "!border-error-200 focus-visible:outline-error-200/75": invalid,
              "bg-neutral-100 cursor-not-allowed": disabled,
              "bg-green-200": hasSelected && theme === SelectInputTheme.Green,
              "bg-turquoise-400": hasSelected && theme === SelectInputTheme.Default
            },
            className
          )}
          tabIndex={0}
          onClick={toggleSelect}
          onBlur={onBlur}
          onMouseDown={e => {
            // Prevent default mousedown behavior that might interfere with focus
            // Only if the div itself is the target (not child buttons)
            if (e.target === e.currentTarget) {
              e.preventDefault();
            }
          }}
        >
          <span
            className={classNames("flex-1 pl-md", {
              "text-t-neutral-400": !hasSelected,
              "text-dark-blue": hasSelected
            })}
          >
            {selected.caption}
          </span>

          <select
            ref={selectInputRef}
            {...{ inert: "" }} // silly workaround because we're not on react ver 19+ and the fix in eslintrc doesn't work here
            className="sr-only"
            name={name}
            id={id}
            value={value || ""}
            title={title}
            disabled={disabled}
            required={required}
            onChange={e => {
              const selectedItem =
                items.find(item => item.value === e.target.value) || defaultOption;
              selectItem(selectedItem);
            }}
            onBlur={onBlur}
          >
            <option disabled value={defaultOption.value}>
              {defaultOption.caption}
            </option>
            {items.map((item, index) => (
              <option key={`options_${index}`} value={item.value} data-f-datainput>
                {item.caption}
              </option>
            ))}
          </select>

          <div
            className={classNames(
              "flex flex-wrap content-center justify-center",
              "w-[3rem] h-[3rem] p-xs ml-auto rounded-tl-br-xs transition-short-out relative"
            )}
          >
            {hasSelected ? (
              <Button
                type={ButtonType.Button}
                color={
                  theme === SelectInputTheme.Green
                    ? ButtonColor.Green
                    : ButtonColor.Turquoise
                }
                size={ButtonSize.SM}
                variant={ButtonVariant.Square}
                icon={"/dist/sprite.svg#sys-close"}
                iconRotated={isOpen}
                iconTransition={true}
                onClick={e => {
                  e.stopPropagation();
                  resetValue();
                }}
              />
            ) : (
              <Button
                id={buttonId}
                type={ButtonType.Button}
                color={ButtonColor.Neutral}
                size={ButtonSize.SM}
                variant={ButtonVariant.Square}
                icon={"/dist/sprite.svg#sys-chevron-down"}
                additionalClass={"[&_svg]:w-lg [&_svg]:h-lg"}
                iconRotated={isOpen}
                iconTransition={true}
              />
            )}
          </div>
        </div>

        <div
          ref={dropdownRef}
          className="absolute z-component w-full mt-sm bg-white rounded-tl-br-sm shadow-md overflow-auto"
          style={{ display: "none" }}
        >
          {allowSearch && (
            <div className="p-sm border-b border-neutral-200">
              <SearchInput
                name={`${id}_search`}
                placeholder={searchPlaceholder}
                onInput={handleSearchChange}
                onSubmit={handleSearchSubmit}
                additionalClassName="text-sm"
              />
            </div>
          )}

          <ul className="p-md divide-y divide-neutral-300 max-h-[40rem]">
            {items.map((item, index) => {
              const isSelected =
                selected && selected.value.toLowerCase() === item.value.toLowerCase();

              return (
                <li
                  key={`results_${index}`}
                  className={classNames(
                    "px-sm py-md cursor-pointer flex items-center justify-between"
                  )}
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    selectItem(item);
                  }}
                >
                  <SelectionItem
                    name={item.caption}
                    value={item.value}
                    labelSubTitle={item.description}
                    labelTitle={item.caption}
                    checked={isSelected}
                  />
                </li>
              );
            })}

            {itemLimitWarning && (
              <li className="p-sm text-t-sm text-t-neutral-600 border-t border-neutral-200">
                <span>{itemLimitWarning}</span>
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SelectInput;
